import { Button, Result } from 'antd'
import { Component, type ErrorInfo, type ReactNode } from 'react'

import { reloadKey } from './chunkReloadGuard'

/**
 * Catches what a lazily loaded route can throw.
 *
 * Routes arrive as separate chunks whose filenames carry a content hash, so a
 * deploy replaces every one of them and the old names stop existing. A tab
 * opened before that deploy still holds the old names: the first navigation to
 * a page it has not loaded yet asks for a file that now returns 404, the
 * dynamic import rejects, and with nothing catching it React unmounts the whole
 * tree. The user sees a white screen and no explanation.
 *
 * That case is recoverable without them understanding any of it — the current
 * HTML names the current chunks — so it reloads once. Everything else is a real
 * bug, and a page that quietly reloads on a real bug is a page that hides it,
 * so those get a message instead.
 */
interface Props {
  children: ReactNode
}

interface State {
  failed: boolean
}



/**
 * A failed dynamic import does not have one canonical shape: the message
 * differs between browsers and between bundlers, and there is no error class to
 * match on. These are the forms it actually takes.
 */
function isChunkLoadFailure(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false
  }
  const text = `${error.name} ${error.message}`
  return (
    /ChunkLoadError/i.test(text) ||
    /Loading chunk \d+ failed/i.test(text) ||
    /Failed to fetch dynamically imported module/i.test(text) ||
    /error loading dynamically imported module/i.test(text) ||
    /Importing a module script failed/i.test(text)
  )
}

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (isChunkLoadFailure(error) && !sessionStorage.getItem(reloadKey)) {
      // Marked before reloading, not after: the reload never comes back here to
      // clear it, and without the mark a chunk that is genuinely missing would
      // reload forever.
      sessionStorage.setItem(reloadKey, '1')
      window.location.reload()
      return
    }
    console.error('Unhandled error in a route', error, info.componentStack)
  }

  render(): ReactNode {
    if (!this.state.failed) {
      return this.props.children
    }

    return (
      <Result
        status="warning"
        title="This page did not load"
        subTitle="Reloading usually fixes it. If it keeps happening, the error is in the console."
        extra={
          <Button
            type="primary"
            onClick={() => {
              sessionStorage.removeItem(reloadKey)
              window.location.reload()
            }}
          >
            Reload
          </Button>
        }
      />
    )
  }
}
